// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { SearchRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { sumBy } from 'lodash-es';
import { Key, useEffect, useMemo, useRef, useState } from 'react';

interface FilterMenuOption<ID> {
  id: ID;
  label: string;
  isActive: boolean;
  isArchived?: boolean;
  color?: string;
  options?: FilterMenuOption<ID>[];
}

interface Props<ID> {
  /**
   * The label to display on the button.
   */
  label: string;

  /**
   * The options to display in the menu.
   */
  options: FilterMenuOption<ID>[];

  /**
   * A callback fired when a single option is toggled.
   */
  onChangeOption: (id: ID, isActive: boolean) => void;

  /**
   * When true, renders a search input at the top of the menu that
   * substring-filters options by `label`. Useful for high-cardinality
   * lists like projects or items where scrolling is impractical. Off
   * by default to preserve the compact menu for short lists.
   *
   * Search applies to flat options and to nested sub-options; group
   * headers are kept when at least one of their children matches so
   * the matched rows stay grouped under their parent label.
   */
  isSearchable?: boolean;

  /**
   * Optional override for the search input's placeholder. Defaults to
   * "Search {label}" so a "Project" menu reads "Search project".
   */
  searchPlaceholder?: string;
}

/**
 * A simple label component that displays "Archived" in a muted style
 */
function ArchivedLabel() {
  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      display="inline"
      sx={{
        textTransform: 'lowercase',
        fontVariant: 'small-caps',
      }}
    >
      {' '}
      Archived
    </Typography>
  );
}

/**
 * A menu component that displays a button with a label and a count of active
 * options. When clicked, the menu displays a list of options.
 */
export function FilterMenu<ID extends Key>(props: Props<ID>) {
  const menu = useDisclosure();
  const anchorElementRef = useRef<HTMLButtonElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Reset the search box when the menu closes so the next open starts
  // fresh — leaving a stale term in place would silently hide options.
  useEffect(() => {
    if (!menu.isOpen) {
      setSearchTerm('');
    }
  }, [menu.isOpen]);

  // Filtered options used for rendering. Active selections are always
  // visible regardless of the search term so the user never loses
  // sight of what's currently applied. For nested groups, keep the
  // group when at least one of its children matches (or is active).
  const filteredOptions = useMemo(() => {
    if (!props.isSearchable) {
      return props.options;
    }
    const needle = searchTerm.trim().toLowerCase();
    if (needle === '') {
      return props.options;
    }
    const matches = (label: string) => label.toLowerCase().includes(needle);
    return props.options
      .map((option) => {
        if (option.options) {
          const subOptions = option.options.filter(
            (sub) => sub.isActive || matches(sub.label)
          );
          if (subOptions.length === 0 && !matches(option.label)) {
            return null;
          }
          return { ...option, options: subOptions };
        }
        return option.isActive || matches(option.label) ? option : null;
      })
      .filter((option): option is FilterMenuOption<ID> => option !== null);
  }, [props.isSearchable, props.options, searchTerm]);

  const isDisabled = props.options.length === 0;

  let count = sumBy(props.options, 'isActive');

  // Bug: sumBy returns a true/false value instead of 0/1 when there is only one option
  // We check for this and use a number instead
  if (props.options.length === 1) {
    count = props.options[0].isActive ? 1 : 0;
  }

  // If this is actually a nested menu, we should be counting the children instead
  if (props.options[0]?.options) {
    let runningCount = 0;
    props.options.map((option) => {
      runningCount += sumBy(option.options, 'isActive');
    });
    count = runningCount;
  }

  return (
    <>
      <FilterMenuButton
        ref={anchorElementRef}
        count={count}
        isDisabled={isDisabled}
        onClick={menu.open}
      >
        {props.label}
      </FilterMenuButton>
      <Menu
        open={menu.isOpen}
        anchorEl={anchorElementRef.current}
        onClose={menu.close}
      >
        {props.isSearchable && (
          <Box sx={{ px: 1.5, py: 1, minWidth: 240 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={
                props.searchPlaceholder ?? `Search ${props.label.toLowerCase()}`
              }
              // Stop the keys MUI's Menu uses for item navigation, plus the
              // single characters that drive its type-ahead — the rows here are
              // direct MenuList children, so a character matching a row's label
              // moves focus onto it and swallows the keystroke. Escape and Tab
              // MUST bubble — Escape closes the menu, Tab cycles focus normally
              // — and a modified character belongs to a global shortcut.
              onKeyDown={(event) => {
                const isTypeAhead =
                  event.key.length === 1 &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.altKey;

                if (
                  event.key === 'ArrowUp' ||
                  event.key === 'ArrowDown' ||
                  event.key === 'Home' ||
                  event.key === 'End' ||
                  isTypeAhead
                ) {
                  event.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded
                      fontSize="small"
                      sx={{ color: 'neutral.400' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        {props.isSearchable && filteredOptions.length === 0 && (
          <MenuItem disabled sx={{ minWidth: 240 }}>
            <Typography variant="body2" color="text.secondary">
              No matches
            </Typography>
          </MenuItem>
        )}
        {filteredOptions.map((option) => {
          if (option.options) {
            return (
              <div key={option.id}>
                <MenuItem
                  sx={{
                    cursor: 'default',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {option.label}
                    {option?.isArchived && <ArchivedLabel />}
                  </Typography>
                </MenuItem>
                {option.options.map((subOption) => (
                  <FilterMenuItem
                    key={subOption.id}
                    isActive={subOption.isActive}
                    onChange={(isActive) =>
                      props.onChangeOption(subOption.id, isActive)
                    }
                    color={subOption.color}
                  >
                    {subOption.label}
                    {subOption?.isArchived && <ArchivedLabel />}
                  </FilterMenuItem>
                ))}
              </div>
            );
          }

          return (
            <FilterMenuItem
              key={option.id}
              isActive={option.isActive}
              onChange={(isActive) => props.onChangeOption(option.id, isActive)}
              color={option.color}
            >
              {option.label}
              {option?.isArchived && <ArchivedLabel />}
            </FilterMenuItem>
          );
        })}
      </Menu>
    </>
  );
}
